"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const benefits = [
  {
    icon: "GlobeAltIcon",
    color: "kenya-red",
    title: "International Exposure",
    description: "Your product featured across 38 Kenyan embassies and consulates worldwide, reaching millions of potential visitors annually.",
    stat: "38 Embassies",
  },
  {
    icon: "MegaphoneIcon",
    color: "kenya-green",
    title: "National Marketing",
    description: "Inclusion in County Product Portal's official digital campaigns, trade fair materials, and international tourism roadshows.",
    stat: "12 Trade Fairs/Year",
  },
  {
    icon: "DevicePhoneMobileIcon",
    color: "kenya-red",
    title: "Digital Platform Listing",
    description: "Featured on the County Product Portal website, mobile app, and partner booking platforms with verified product profiles.",
    stat: "2.4M Monthly Visitors",
  },
  {
    icon: "DocumentTextIcon",
    color: "kenya-green",
    title: "Professional Profiling",
    description: "Ministry-designed downloadable product profile sheets for use in B2B meetings, travel agent pitches, and media kits.",
    stat: "Print-Ready PDFs",
  },
  {
    icon: "UserGroupIcon",
    color: "kenya-red",
    title: "Network Access",
    description: "Connect with tour operators, travel agents, and investors through the Ministry's curated stakeholder network.",
    stat: "500+ Tour Operators",
  },
  {
    icon: "ShieldCheckIcon",
    color: "kenya-green",
    title: "Government Endorsement",
    description: "Official Ministry approval stamp adds credibility and trust — essential for international marketing partnerships.",
    stat: "Official Certification",
  },
];

export default function WhySubmitSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".reveal-item");
            items.forEach((item, i) => {
              setTimeout(() => {
                item.classList.add("revealed");
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-warm-bg relative overflow-hidden">
      {/* Background text watermark */}
      <div className="absolute -top-8 -right-8 text-[20vw] font-display font-bold text-kenya-black/[0.025] leading-none pointer-events-none select-none uppercase">
        Kenya
      </div>

      <div className="max-w-8xl mx-auto relative z-10">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-end mb-12 sm:mb-20 reveal-item reveal-hidden">
          <div className="space-y-4 sm:space-y-5">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-kenya-red/10 border border-kenya-red/20">
              <div className="w-2 h-2 rounded-full bg-kenya-red" />
              <span className="text-[0.6875rem] font-bold uppercase tracking-[0.4em] text-kenya-red font-condensed">
                Why Submit
              </span>
            </div>
            <h2
              className="font-display font-bold text-kenya-black"
              style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)", lineHeight: "0.92", letterSpacing: "-0.025em" }}
            >
              Put Kenya's Best
              <br />
              <span className="font-display italic font-light text-kenya-red">on the World Stage</span>
            </h2>
          </div>
          <p className="text-text-muted text-base sm:text-lg leading-relaxed max-w-md">
            From Kakamega Forest to Lake Turkana, every unique Kenyan experience deserves international recognition. The Ministry's submission portal is your direct gateway to global tourism marketing.
          </p>
        </div>

        {/* Benefits grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {benefits.map((benefit, idx) => (
            <div
              key={benefit.title}
              className={`reveal-item reveal-hidden group p-6 sm:p-8 rounded-xl2 bg-white border-2 ${
                benefit.color === "kenya-red" ?"border-kenya-red/10 hover:border-kenya-red/30 hover:shadow-card-red" :"border-kenya-green/10 hover:border-kenya-green/30 hover:shadow-card-green"
              } transition-all duration-500 cursor-default`}
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <div className="flex items-start justify-between mb-5 sm:mb-6">
                <div
                  className={`w-13 h-13 w-12 h-12 rounded-xl flex items-center justify-center ${
                    benefit.color === "kenya-red" ?"bg-kenya-red/10 group-hover:bg-kenya-red/18" :"bg-kenya-green/10 group-hover:bg-kenya-green/18"
                  } transition-colors`}
                >
                  <Icon
                    name={benefit.icon as Parameters<typeof Icon>[0]["name"]}
                    size={24}
                    className={benefit.color === "kenya-red" ? "text-kenya-red" : "text-kenya-green"}
                  />
                </div>
                <span
                  className={`text-[0.625rem] sm:text-[0.6875rem] font-bold uppercase tracking-[0.25em] px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full font-condensed ${
                    benefit.color === "kenya-red" ?"bg-kenya-red/10 text-kenya-red" :"bg-kenya-green/10 text-kenya-green"
                  }`}
                >
                  {benefit.stat}
                </span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-kenya-black mb-2 sm:mb-3 font-display">
                {benefit.title}
              </h3>
              <p className="text-text-muted text-[0.875rem] sm:text-[0.9375rem] leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}